import { ImageName } from "@mcleod/images";
import { LogManager, ModelRow, StringUtil } from "@mcleod/core";
import {
    DataDisplayEvent, Label, Layout, LookupModelSelectionEvent, Tab, Table, TableRow, TableRowDisplayEvent, TableRowMode, Textbox
} from "@mcleod/components";
import { LMEUserSettings } from "@mcleod/general";
import { QualifierCodeQuickInfo } from "@mcleod/dispatch";
import { AutogenLayoutEdiStopNotesAndReferenceNumbers } from "./autogen/AutogenLayoutEdiStopNotesAndReferenceNumbers";

const log = LogManager.getLogger("lme/datafusion/EdiStopNotesAndReferenceNumbers");

//**TODO change model for edi-ref-number to look up description.
export class EdiStopNotesAndReferenceNumbers extends AutogenLayoutEdiStopNotesAndReferenceNumbers {
    private _stopRow: ModelRow<any>;
    private _partnerId: string;
    private _version: string;


    override onLoad(): void {
        if (this._designer != null) return;
        this.tabsetStopNotesRefNos.selectedIndex = -1;
        this.styleTab(this.tabStopComments);
        this.styleTab(this.tabStopRefNumbers);
    }

    set stopRow(data: ModelRow<any>) {
        if (data != this._stopRow) {
            this._stopRow = data;
            this.syncStop();
        }
    }

    get stopRow(): ModelRow<any> {
        return this._stopRow;
    }

    private syncStop() {
        this.tableStopComments.clearRows();
        this.tableStopReferenceNumbers.clearRows();
        if (this.stopRow != null) {
            this.addTableRowCreationListener(this.tableStopComments)
            this.addTableRowCreationListener(this.tableStopReferenceNumbers)
            this.addTableRows(this.tableStopComments, this.stopRow.get("edi_stop_notes"));
            this.addTableRows(this.tableStopReferenceNumbers, this.stopRow.get("edi_ref_number"));
        }
        this.tableStopComments.emptyCaptionBeforeSearch = "";
        this.tableStopReferenceNumbers.emptyCaptionBeforeSearch = "";
    }

    private addTableRowCreationListener(table: Table) {
        table.addRowCreateListener(event => {
            this.updateStopsTabCaption(table)
            if (event.getTableRow().data._appending)
                event.getTableRow().data.set("stop_id", this.stopRow.get("id"));
            if (table.quickAddRow?.findComponentById("fireImage") != null && table.quickAddRow?.findComponentById("fireImage").visible == true)
                table.quickAddRow.findComponentById("fireImage").visible = false;
        });
    }

    private addTableRows(table: Table, data: any[]) {
        if (data != null && data.length > 0) {
            table.dataSource.preventChangeNotifications = true;
            for (const [key, value] of Object.entries(data))
                this.addTableRow(table, value, data.length);
        }
    }

    private addTableRow(table: Table, value: any, rowCount: number = -1): Promise<any> {
        return table._createNewRowData().then(modelRow => {
            modelRow.setValues(value instanceof ModelRow ? value.data : value);
            modelRow._appending = value instanceof ModelRow ? value._appending : false;
            table.addRow(modelRow, { mode: TableRowMode.NONE }, { display: true, addToData: table.dataSource.isAddingOrUpdating() });
            if (table.rows.length == rowCount) {
                table.dataSource.updateOriginalRowCount();
                table.dataSource.preventChangeNotifications = false;
            }
        });
    }

    tableStopReferenceNumbersOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        if (tableRow?.mode === TableRowMode.QUICK_ADD && tableRow.data?.get("send_to_driver") == null) {
            const textboxSendToDriver = tableRow.findComponentById("textboxSendToDriver") as Textbox;
            textboxSendToDriver.setSelectedItemFromValue("N");
            textboxSendToDriver.updateBoundData(tableRow.data, tableRow.table.dataSource.mode);
        }
        const tbRefQual = tableRow.findComponentById("textboxReferenceQual") as Textbox;
        tbRefQual.addLookupModelSelectionListener(this.tbRefQualOnLookupModelSelection);
        tbRefQual.quickInfoOverrideMethod = async () => {
            const lookupData = tbRefQual?.getFirstLookupModelData();
            if (tbRefQual == null || lookupData == null)
                return null;
            const layout = await Layout.getLoadedLayout("lme/dispatch/QualifierCodeQuickInfo") as QualifierCodeQuickInfo;
            //The EDI side has no customer context, so always resolve the qualifier from the shared
            //edi_standard_code metadata table scoped by the EDI order's version.
            layout.showLink = false;
            layout.textboxStandardCode.visible = false;
            layout.textboxDescription.visible = false;
            layout.textboxStandardCodeSC.visible = true;
            layout.textboxDescriptionSC.visible = true;
            await layout.sourceEdiStandardCode.search({
                standard_code: lookupData.get("standard_code"),
                element_input: lookupData.get("element_id") ?? "128",
                version_input: this.version ?? "004010"
            });
            return layout;
        };
    }

    private tbRefQualOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.selection != null) {
            const tableRow = TableRow.getContainingTableRow(event.target);
            if (tableRow != null)
                tableRow.data.set("description", event.selection.get("description"));
        }
    }

    updateStopsTabCaption(table: Table) {
        const tab = table.parent as Tab;
        let hotCommentCount = 0;
        if (tab != null && tab["countLabel"] != null) {
            tab["countLabel"].text = table.rows.length + "";
            for (const tableRow of table.rows) {
                if (!tableRow.data?.isNull("comment_type") && "HC" === tableRow.data?.get("comment_type"))
                    hotCommentCount++;
            }
            if (hotCommentCount > 0 && !tab["_heading"].contains(tab["hotCommentCountLabel"])) {
                tab["countLabel"].fillRow = false;
                tab["countLabel"].marginRight = 18;
                tab["hotCommentCountLabel"] = new Label({
                    fontBold: true, text: hotCommentCount.toString(), fillRow: true,
                    color: "primary", rowBreak: false
                });
                tab["hotCommentCountLabel"].imageName = ImageName.fire;
                tab["_heading"].insert(tab["hotCommentCountLabel"], 2);
            }
            else if (hotCommentCount == 0 && tab["_heading"].contains(tab["hotCommentCountLabel"])) {
                tab["_heading"].remove(tab["hotCommentCountLabel"]);
                tab["countLabel"].fillRow = true;
            }
            else if (hotCommentCount > 0 && tab["_heading"].contains(tab["hotCommentCountLabel"]))
                tab["hotCommentCountLabel"].text = hotCommentCount.toString();

        }
    }

    textboxReferenceQualBeforeLookupModelSearch(event) {
        event.filter.partner_id = this._partnerId;
        event.filter.version = this._version;
        event.filter.code_type = "Qualifier";
    }

    async textboxReferenceQualOnBlur(event) {
        let sendToDriver = "N";
        let description = "";
        try {
            const data = event.target.lookupModelData[0].data;
            sendToDriver = StringUtil.isEmptyString(data.send_to_driver) ? "N" : data.send_to_driver;
            description = data.description;
        } catch (reason) { }

        const currRefnoRow = event.target.row as TableRow;
        const modelRow = currRefnoRow.data as ModelRow;
        modelRow.set("send_to_driver", sendToDriver);
        if (!StringUtil.isEmptyString(description))
            modelRow.set("description", description);

        const textbox = currRefnoRow.findComponentById("textboxSendToDriver") as Textbox;
        textbox.setSelectedItemFromValue(sendToDriver);
    }

    saveChangesFromEdit(source: EdiStopNotesAndReferenceNumbers) {
        this.updateSourceTables(this.tableStopComments, source.tableStopComments)
        this.updateSourceTables(this.tableStopReferenceNumbers, source.tableStopReferenceNumbers)
    }

    updateSourceTables(thisTable: Table, sourceTable: Table) {
        const dataSource = thisTable.dataSource;
        const sourceTableMap = new Map(sourceTable.rows.map((row) => [row.data.get("id"), row]));
        if (dataSource.deletedData != null) {
            for (const row of dataSource.deletedData) {
                const rowToDelete = sourceTableMap.get(row.get("id"));
                if (rowToDelete != null)
                    rowToDelete.deleteRow();
            }
        }
        const myRows = thisTable.rows;
        for (const tableRow of myRows) {
            const sourceTableRow = sourceTableMap.get(tableRow.data.get("id"));
            if (sourceTableRow == null)
                this.addTableRow(sourceTable, tableRow).then(() => this.updateStopsTabCaption(sourceTable));
            else
                sourceTableRow.data.setValues(tableRow.data);
        }
        sourceTable.displayData(null, sourceTable.data, 0);
        this.updateStopsTabCaption(sourceTable);
    }

    styleTab(tab: Tab) {
        if (tab.heading != null && tab.headingLabel != null) {
            tab.heading.color = "subtle.light";
            tab.headingLabel.setProps({ color: "default", fontBold: true, fillRow: false })
            if (tab["countLabel"] == null) {
                tab["countLabel"] = new Label({
                    fontBold: true, text: "0", fillRow: true,
                    color: "primary", rowBreak: false, marginLeft: 5
                });
                tab.heading.insert(tab["countLabel"], 1);
            }
        }
    }

    /** This is an event handler for the onDataDisplay event of labelEnteredUserId.  */
    labelEnteredUserIdOnDataDisplay(event: DataDisplayEvent) {
        let name = event.rowData.getFirstLookupModelData("entered_user_id")?.get("name");
        if (name == null) {
            const lmData = LMEUserSettings.getUserLookupModelData();
            event.rowData.setLookupModelData("entered_user_id", lmData);
            name = lmData.get("name") || event.rowData?.get("entered_user_id");
        }
        (event.target as Label).text = name;
    }

    /** This is an event handler for the onDataDisplay event of textboxComments.  */
    textboxCommentsOnDataDisplay(event: DataDisplayEvent) {
        if (event.rowData?.get("comment_type") == "HC") {
            const tableRow = TableRow.getContainingTableRow(event.target as Textbox);
            tableRow.findComponentById("fireImage").visible = true;
        }
    }
    /** This is an event handler for the onChange event of textboxCommentType.  */
    textboxCommentTypeOnChange(event) {
        const component = event.target.row?.cells[1] as Textbox;
        if (event.newValue == "Hot comment" && event.userInitiatedChange == true) {
            event.target.row.findComponentById("fireImage").visible = true;
        }
        else if (event.newValue != "Hot comment" && event.userInitiatedChange == true) {
            event.target.row.findComponentById("fireImage").visible = false;
        }
    }

    /** This is an event handler for the onContentsChanged event of tableStopComments.  */
    tableStopCommentsOnContentsChanged(event) {
        this.updateStopsTabCaption(event.getTable());
    }

    public set version(value: string) {
        this._version = value;
    }
    public get version(): string {
        return this._version;
    }
    public set partnerId(id: string) {
        this._partnerId = id;
    }
    public get partnerId(): string {
        return this._partnerId;
    }

}
